import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtNum } from "../lib/api";
import { useLang } from "../i18n";
import { Users, Video, Radio, BadgeCheck } from "lucide-react";
import { motion } from "framer-motion";

const AVATARS = [
  "https://images.pexels.com/photos/15367435/pexels-photo-15367435.jpeg",
  "https://images.unsplash.com/photo-1644353224392-7e532d7b8f4b",
  "https://images.pexels.com/photos/6919947/pexels-photo-6919947.jpeg",
];

export default function Discover() {
  const [streamers, setStreamers] = useState(null);
  const { t } = useLang();

  useEffect(() => {
    api.get("/streamers").then(({ data }) => setStreamers(data)).catch(() => setStreamers([]));
  }, []);

  return (
    <div data-testid="discover-page" className="max-w-7xl mx-auto px-6 py-14">
      <span className="text-xs uppercase tracking-[0.25em] font-bold text-emerald-400">{t("discover_badge")}</span>
      <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mt-3 mb-12">{t("discover_title")}</h1>
      {streamers === null ? (
        <p className="text-zinc-500">{t("loading")}</p>
      ) : streamers.length === 0 ? (
        <p className="text-zinc-500">{t("discover_empty")}</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {streamers.map((s, i) => (
            <motion.div key={s.user_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
              <Link to={`/streamer/${s.user_id}`} data-testid="streamer-card"
                className="block bg-zinc-900 border border-zinc-800/60 rounded-2xl overflow-hidden hover:border-emerald-700/60 transition-colors group">
                <div className="h-36 relative overflow-hidden">
                  <img src={s.channel.avatar_url || AVATARS[i % AVATARS.length]} alt={s.name}
                    className="w-full h-full object-cover opacity-60 group-hover:opacity-80 transition-opacity" />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 to-transparent" />
                  {s.channel.is_live && (
                    <span className="absolute top-4 right-4 live-pulse inline-flex items-center gap-1.5 bg-red-500 text-white text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full">
                      <Radio size={11} /> {t("live")}
                    </span>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2">
                    <h3 className="text-xl font-semibold text-white">{s.name}</h3>
                    <BadgeCheck size={18} className="text-emerald-400" />
                  </div>
                  <p className="text-xs text-zinc-500 capitalize mt-1">{s.channel.platform} · @{s.channel.handle}</p>
                  <div className="flex gap-5 mt-4 text-sm text-zinc-400">
                    <span className="flex items-center gap-1.5"><Users size={14} /> {fmtNum(s.channel.followers)}</span>
                    <span className="flex items-center gap-1.5"><Video size={14} /> {fmtNum(s.channel.videos)}</span>
                  </div>
                  <p className="text-xs text-emerald-400 mt-4 font-semibold">{t("discover_tiers", { n: s.tiers.length })}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
